import { cn } from '@/shared/lib/utils';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import * as React from 'react';

/**
 * 우측 드로어(M-12, 데스크톱 640) · 하단 시트(M-11 모바일 필터) · 모바일 GNB 메뉴.
 * Radix Dialog 기반이라 포커스 가두기 · ESC · body 스크롤 잠금을 기본 제공한다.
 */
const Sheet = DialogPrimitive.Root;
const SheetTrigger = DialogPrimitive.Trigger;
const SheetClose = DialogPrimitive.Close;
const SheetTitle = DialogPrimitive.Title;
const SheetDescription = DialogPrimitive.Description;

type SheetSide = 'right' | 'bottom' | 'left';

const sideClassName: Record<SheetSide, string> = {
  right: 'inset-y-0 right-0 h-full w-full lg:w-[640px]',
  bottom: 'inset-x-0 bottom-0 max-h-[85vh] w-full rounded-t-card',
  left: 'inset-y-0 left-0 h-full w-[280px]',
};

interface SheetContentProps extends React.ComponentPropsWithoutRef<
  typeof DialogPrimitive.Content
> {
  side?: SheetSide;
  /** v1.0 디자인은 드로어 · 팝업 모두 검정 50% 딤 (Figma M-12 · M-22 dim) — 값은 호환용으로 남겨 둔다 */
  overlay?: 'drawer' | 'popup';
}

const SheetContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  SheetContentProps
>(({ side = 'right', overlay = 'drawer', className, ...props }, ref) => (
  <DialogPrimitive.Portal>
    <DialogPrimitive.Overlay
      data-overlay={overlay}
      className="fixed inset-0 z-50 bg-dim-50"
    />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        'fixed z-50 flex flex-col overflow-hidden bg-surface outline-none',
        sideClassName[side],
        className,
      )}
      {...props}
    />
  </DialogPrimitive.Portal>
));
SheetContent.displayName = 'SheetContent';

/**
 * Figma 라이브러리 Bottom Sheet (Footer Item) (905:13663) — 드로어 · 시트 맨 아래 버튼 줄.
 * 흰 배경 · 위 16 / 좌우 24 / 아래 40 · 버튼 간격 8. 버튼은 Giant(56)로 넣고 flex-1로 나눈다.
 * 예: <SheetFooter><Button variant="grey" size="giant">취소</Button><Button size="giant">등록</Button></SheetFooter>
 */
const SheetFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex shrink-0 gap-2 bg-surface-white px-6 pt-4 pb-10 [&>*]:flex-1',
      className,
    )}
    {...props}
  />
);

export {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetTitle,
  SheetTrigger,
};
